import { Injectable, Injector } from '@angular/core';
import { ProjectService } from '../project/project.service';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { Building } from './building.interface';
import { first, filter } from 'rxjs/operators';
import { ApiService } from '../../api/api.service';
import { Area } from '../area/area.interface';
import { BasicArea } from '../basic-area/basic-area.interface';
import { Cluster } from '../cluster/cluster.interface';
import { ElementService } from '../element/element.service';

/**
 * Building core service.
 *
 * Contains all the private logic for Building management.
 *
 * **Only BuildingServcice is allowed to access this Service.**
 */
@Injectable({
  providedIn: 'root'
})
export class BuildingCoreService {

  /** Building subject. */
  protected _building: Subject<Building[]> = new BehaviorSubject<Building[]>(null);

  /** Building observable. */
  public building$ = this._building.asObservable();

  constructor(
    protected apiService: ApiService,
    protected projectService: ProjectService,
  ) {
    this._trackProject();
  }

  /**
   * Get buildings.
   *
   * @returns The current buildings.
   */
  public get(): Building[] {
    let buildings: Building[];
    this.building$.pipe(first()).subscribe(p => buildings = p);
    return buildings;
  }

  /**
   * Get specific building.
   *
   * @param id The id of the building.
   * @returns The requested building.
   */
  public getBuilding(id: Building['_id']): Building {
    return this.get().find(b => b._id === id);
  }

  /**
   * Get a building's properties.
   *
   * Greatly reduces size of the returned building.
   *
   * @note Fallback has been added for projects where elements are `projectLevel: 2`,
   *  but `constructionId` points to project.
   * @todo Fallback should be removed once resolved in ticket SPEC-2604.
   *
   * @param id The id of the building.
   * @returns The requested building.
   */
  public getBuildingProperties(id: Building['_id']): Partial<Building> {
    const fallback = {} as Partial<Building>;
    const building = JSON.parse(JSON.stringify(this.getBuilding(id) || fallback)) as Building;
    for (const prop of Object.keys(building)) {
      if (typeof building[prop] === 'object' || Array.isArray(building[prop])) {
        delete building[prop];
      }
    }
    return building;
  }

  /**
   * Get building by area id.
   *
   * @param id Area id.
   * @returns The parent building for the area.
   */
  public getBuildingByAreaId(id: Area['_id']): Building {
    const buildings = JSON.parse(JSON.stringify(this.get())) as Building[];
    for (const building of buildings) {
      for (const cluster of building.clusters) {
        if (cluster.areas.find(a => a._id === id)) {
          return building;
        }
      }
    }
  }

  /**
   * Get parent building by area template id.
   *
   * @param id Basic area id.
   * @returns The parent building.
   */
  public getBuildingByBasicAreaId(id: BasicArea['_id']): Building {
    const buildings = JSON.parse(JSON.stringify(this.get())) as Building[];
    for (const building of buildings) {
      for (const cluster of building.clusters) {
        if (cluster.areas.find(a => a.basicAreaId === id)) {
          return building;
        }
      }
    }
  }

  /**
   * Get parent building by cluster id.
   *
   * @param id Cluster id.
   * @returns The parent building.
   */
  public getBuildingByClusterId(id: Cluster['_id']): Building {
    const buildings = JSON.parse(JSON.stringify(this.get())) as Building[];
    for (const building of buildings) {
      if (building.clusters.find(c => c._id === id)) {
        return building;
      }
    }
  }

  /**
   * Delete building from project.
   *
   * When deleting a building, all it's children should be deactivated too
   *  - clusters
   *    - areas
   *      - elements/requirements (does not happen yet!)
   *
   * @todo Remove requirements upon deleting building (SPEC-2825):
   *  - Create a local loop/patch method for updating a lot of elements *at once*, to prevent
   *    firing the project$ observable to trigger hundreds(?) of times, Or:
   *  - Create a project.refresh() method, which just requests a new project object from the
   *    server, to prevent a lot of work on the front-end.
   *
   * @param building The building to delete.
   * @returns Whether the delete is successful.
   */
  protected _delete(building: Building): Observable<boolean> {
    return new Observable(observer => {
      this.apiService.deleteBuilding(building).subscribe(response => {
        building = {
          ...building,
          active: false,
          clusters: building.clusters.map(c => {
            return {
              ...c,
              active: false,
              areas: c.areas.map(a => {
                return {
                  ...a,
                  active: false
                };
              })
            };
          })
        };
        this.projectService.patchBuilding(building, response);
        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * Patch building.
   *
   * @todo Move Toast, and set config param.
   * @todo Remove method and move logic to ProjectService.
   *
   * @param values The updated values
   * @returns Observable with state.
   */
  protected _patchBuilding(building: Building, values: Partial<Building>, config?: any): Observable<boolean> {
    return new Observable(observer => {
      this.apiService.updateBuilding(building, values).subscribe(response => {
        this.projectService.patchBuilding(building, response);
        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * Track project and set buildings.
   */
  protected _trackProject(): void {
    this.projectService.project$.pipe(
      filter(project => project !== null)
    ).subscribe(project => {
      this._pipeline(project.buildings);
      this._building.next(project.buildings);
    });
  }

  /**
   * Pipeline for buildings.
   *
   * After each update to the project, update each building where needed.
   *
   * @todo Move to BuildingPipelineService?
   *
   * @param buildings The buildings to check.
   */
  private _pipeline(buildings: Building[]) {
    this._pipelineRatio(buildings);
    this._pipelineRemoveInactiveClusters(buildings);
  }

  /**
   * Watch the building Ratio, BVO and FNO.
   *
   * @param buildings The buildings to check.
   */
  private _pipelineRatio(buildings: Building[]): void {
    let totalFno = 0;
    let totalBvo = 0;

    for (const building of buildings) {
      for (const cluster of building.clusters) {
        if (cluster.active) {
          let clusterFno = 0;
          for (const area of cluster.areas) {
            if (area.functionality === 1) {
              clusterFno += area.surface * area.quantity;
            }
          }
          totalFno += clusterFno;
          totalBvo += clusterFno * cluster.ratio;
        }
      }

      // Patch building when has changed.
      if (totalBvo && totalFno && building.ratio !== totalBvo / totalFno) {
        building.ratio = totalBvo / totalFno;
        this.apiService.updateBuilding(building, { ratio: building.ratio });
      }
    }
  }

  /**
   * Remove inactive clusters from building.
   *
   * @param buildings The buildings to remove the inactive clusters from.
   */
  private _pipelineRemoveInactiveClusters(buildings: Building[]) {
    for (const building of buildings) {
      building.clusters = building.clusters.filter(c => c.active);
    }
  }
}
